import React, { useContext, useState, useMemo } from "react";
import { MDBCol, MDBRow } from "mdbreact";
import BreadCrumb from "../../../../common/breadCrumb/BreadCrumb";
import "./BulkExport.scss";
import { AbsCheckBox, Button, COLORS, Label, Modal } from "abs-ui-components";
import { PropTypes } from "prop-types";
import AutocompleteDropdown from "../../../../common/autocompleteDropdown/AutocompleteDropdown";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { ClaimsContext } from "../../../../services/ClaimsService";
import { ClaimType } from "../../../../constants/claims";
import BulkExportList from "./BulkExportList";
import { emitToasterMsg } from "../../../../services/toasterUtility";
import { createBulkExport, WCNList } from "../../../../services/AdminService";
import { TextField } from "@mui/material";
import { TextArea } from "abs-ui-components";
import { FeatureType } from "../../../../constants/FeatureToggles";
import { useNavigate } from "react-router-dom";
import { getYearOptions } from "../../../../services/utilities";
const BulkExportPage = () => {
	const { userHasClaim, userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
	const [refreshTable, setRefreshTable] = React.useState(false);
	const toggleRefreshTable = () => {
		setRefreshTable((prev) => !prev);
	};
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [selected, setSelected] = useState([]);
	const isAdmin = userHasClaim(ClaimType.VIEW_ADMIN);
	const canSeeContainer = isAdmin && userFeaturesAreLoaded && userHasFeature(FeatureType.BulkExport);
	const navigate = useNavigate();

	const breadcrumb = useMemo(
		() => [
			{
				label: "Admin",
				url: "",
				navId: "admin_navid",
			},
			{
				label: "Bulk Export",
				url: "",
				navId: "bulk_export_navid",
			},
		],
		[]
	);
	const handleOk = () => {
		navigate("/dashboard");
	};
	const handleFormSubmit = async (data) => {
		try {
			setIsSubmitting(true);
			const payload = {
				wcnMstId: data?.wcn?.wcnMappingId,
				reportingYear: data?.reportingyear?.value,
				vesselImoNumbers: data?.vesselimonumbers,
				regulationTypes: data?.regulationtypes,
			};

			const response = await createBulkExport(payload);
			if (response?.status === 200) {
				emitToasterMsg("success", "Success", "Submitted successfully");
				toggleRefreshTable();
				methods.reset();
			}
		} catch (error) {
			const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong while creating bulk export";
			emitToasterMsg("error", "Error", errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};
	const methods = useForm({
		defaultValues: {
			wcn: null,
			vesselimonumbers: "",
			reportingyear: null,
			regulationtypes: [],
		},
	});
	const { trigger, getValues } = methods;
	const reportingYearLoadOptions = async () => {
		let yearOptions = getYearOptions(true);
		return yearOptions.filter(x => x.value >= 2025);
	};
	const regulationOptions = [
		{ label: "DCS", value: "DCS" },
		{ label: "EU MRV", value: "EUMRV" },
		{ label: "UK MRV", value: "UKMRV" },
		{ label: "FEU", value: "FEU" },
		{ label: "UK ETS", value: "UKETS" },
	];
	const wcnLoadOptions = async (inputValue) => {
		try {
			const response = await WCNList(inputValue);
			const formattedWCNOptions = response?.map((item) => ({
				label: `${item.CompanyName}`,
				value: `${item.WcnNumber}`,
				wcnMappingId: item.WcnMstId,
			}));
			return formattedWCNOptions;
		} catch (error) {
			emitToasterMsg("error", "Error", error.message);
			return [];
		}
	};
	const errors = methods.formState.errors;
	const ErrorMessage = ({ name }) => ErrorMsg({ errors, name });

	const formProperties = {
		reportingYear: "reportingyear",
		wcn: "wcn",
		vesselImoNumbers: "vesselimonumbers",
		regulationTypes: "regulationtypes",
	};

	const handleCheckboxChange = (value) => {
		setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
	};
	return (
		<section className="p-4 bulk-export">
			<BreadCrumb items={breadcrumb} />

			{canSeeContainer ? (
				<>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(handleFormSubmit)}>
							<MDBRow className="mt-4 px-3 justify-content-center top-container">
								<MDBCol md="6">
									<MDBRow className="d-flex my-4 wcn">
										<MDBCol md="4" className="my-auto">
											<Label name="Reporting Year :" bold required />
										</MDBCol>
										<MDBCol md="8" className="verifier-dropdown">
											<Controller
												name={formProperties.reportingYear}
												rules={{
													required: "Reporting Year is required",
												}}
												control={methods.control}
												render={({ field }) => {
													return (
														<>
															<AutocompleteDropdown
																className="inputHeight w-100"
																id="management_company_wcn"
																isClearable
																value={field.value ?? null}
																onChange={field.onChange}
																loadOptions={reportingYearLoadOptions}
																getOptionLabel={(option) => option.label}
																placeholder={"Please Select Reporting Year"}
															/>
															<ErrorMessage name={formProperties.reportingYear} />
														</>
													);
												}}
											></Controller>
										</MDBCol>
									</MDBRow>
									<MDBRow className="d-flex my-4 wcn">
										<MDBCol md="4" className="my-auto">
											<Label name="WCN :" bold required />
										</MDBCol>
										<MDBCol md="8" className="verifier-dropdown">
											<Controller
												name={formProperties.wcn}
												rules={{
													required: "WCN is required",
												}}
												control={methods.control}
												render={({ field }) => {
													return (
														<>
															<AutocompleteDropdown
																className="inputHeight w-100"
																id="management_company_wcn"
																isClearable
																value={field.value ?? null}
																onChange={field.onChange}
																loadOptions={wcnLoadOptions}
																getOptionLabel={(option) => `${option.value} - ${option.label}`}
																placeholder={"Please Select WCN"}
															/>
															<ErrorMessage name={formProperties.wcn} />
														</>
													);
												}}
											></Controller>
										</MDBCol>
									</MDBRow>
									<MDBRow className="d-flex my-4 wcn">
										<MDBCol md="4" className="my-auto">
											<Label name="Regulation Type :" bold required />
										</MDBCol>

										<MDBCol md="8">
											<Controller
												name={formProperties.regulationTypes}
												control={methods.control}
												defaultValue={[]}
												rules={{
													validate: (value) => value?.length > 0 || "At least one regulation type is required",
												}}
												render={({ field }) => {
													const selected = field.value || [];
													const handleChange = (value) => {
														console.log("clicked:", value);
														if (selected.includes(value)) {
															field.onChange(selected.filter((v) => v !== value));
														} else {
															field.onChange([...selected, value]);
														}
													};

													return (
														<>
															<div className="regulation-checkbox-group">
																{regulationOptions.map((option) => (
																	<div key={option.value} style={{ display: "flex" }}>
																		<AbsCheckBox
																			id={option.label}
																			label={option.label}
																			slim={true}
																			checked={selected.includes(option.value)}
																			handleChange={(e) => { handleChange(option.value) }}

																		/>
																	</div>
																))}
															</div>

															<ErrorMessage name={formProperties.regulationTypes} />
														</>
													);
												}}
											/>
										</MDBCol>
									</MDBRow>
									<MDBRow className="d-flex my-4 wcn">
										<MDBCol md="4" className="my-auto">
											<Label name="IMO Numbers :" bold />
										</MDBCol>
										<MDBCol md="8" className="mt-4">
											<Controller
												name={formProperties.vesselImoNumbers}
												control={methods.control}
												rules={{
													validate: (value) => {
														if (!value || value.trim() === "") return true;

														const regex = /^\d+(,\s*\d+)*$/;
														return regex.test(value.trim()) || "Enter valid comma separated IMO numbers";
													},
												}}
												render={({ field }) => (
													<>
														<TextArea
															className="imo-textarea"
															placeholder="Please Enter IMO Numbers"
															value={field.value || ""}
															onChange={(e) => field.onChange(e?.target ? e.target.value : e)}
														/>

														<ErrorMessage name={formProperties.vesselImoNumbers} />

														<div className="imo-helper-text">
															User needs to enter the IMO numbers comma-delimited. If nothing is entered its all IMOs.
														</div>
													</>
												)}
											/>

											<MDBRow className="mt-4 status-btn">
												<MDBCol className="col-12 d-flex justify-content-end">
													<button type="submit" className="save-button w-auto px-3" disabled={isSubmitting}>
														{isSubmitting ? "Requesting Download..." : "Request Download"}
													</button>
												</MDBCol>
											</MDBRow>
										</MDBCol>
									</MDBRow>
								</MDBCol>
							</MDBRow>
						</form>
					</FormProvider>
					<div className="mt-4 z-0">
						<BulkExportList refresh={refreshTable} />
					</div>
				</>
			) : (
				<div>
					<Modal
						isOpen={!canSeeContainer}
						modalHeader
						padding={"70px"}
						headerHeight={"50px"}
						showFooter
						footer={
							<div className="d-flex justify-content-end fixed-action-btns p-4">
								<Button
									className="modal-delete-btn"
									onClick={handleOk}
									primaryColor={COLORS.RED.DARK}
									primaryBackgroundColor={COLORS.RED.LIGHT}
								>
									Ok
								</Button>
							</div>
						}
					>
						<div>You are not authorized to view this.</div>
					</Modal>
				</div>
			)}
		</section>
	);
};

const ErrorMsg = ({ errors, name }) => {
	return errors[name] ? <div className="error-msg">{errors[name].message}</div> : null;
};

ErrorMsg.propTypes = {
	name: PropTypes.string.isRequired,
	errors: PropTypes.object.isRequired,
};

export default BulkExportPage;
